import { QueryFactory, useEnabledDataBlockTypes } from '@lmt/shared-types';
import { DataBlockVariant } from '@lmt/shared-types';
import { useTabId } from '@lmt/unicorn-shared/app/components/keep-alive/TabContext';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryMeta } from '@tanstack/react-query';
import { patientInformationDataBlockTypeName } from './components/measurements/common';
import { useSubtenants } from './features/subtenants/selectors';

export function usePrefetchQueries() {
  const dataBlockTypes = useEnabledDataBlockTypes();
  const patientInformationDataBlockType = dataBlockTypes?.find(
    (x) =>
      x.variant === DataBlockVariant.Patient &&
      x.value === patientInformationDataBlockTypeName,
  );
  QueryFactory.DataBlockTypeQuery.useGetUIConfigsQuery(
    patientInformationDataBlockType?.id!,
    {
      enabled: !!patientInformationDataBlockType?.id,
    },
  );
  useSubtenants();
}

function shouldInvalidate(
  meta: QueryMeta,
  currentTabId: string,
  patientTab?: string,
  caseId?: number | null,
) {
  return (
    meta.tabId === currentTabId &&
    (!meta.patientTab || meta.patientTab === patientTab) &&
    (!meta.caseId || meta.caseId == caseId)
  );
}

/*
 * Invalidates queries when switching between tabs
 */
export function useInvalidateQueries() {
  const queryClient = useQueryClient();
  const currentTabId = useTabId();
  const patientTab = pages.patientProfileWithTab.useMatch()?.params?.tabName;
  const caseId = pages.patientCases.useMatch()?.params?.caseId;
  useEffect(() => {
    if (!currentTabId) return;
    queryClient
      .invalidateQueries({
        type: 'active',
        predicate: (query) => {
          if (!query.meta) return false;

          const result =
            query.isStale() &&
            shouldInvalidate(query.meta, currentTabId, patientTab!, caseId);
          return result;
        },
      })
      .catch((e) => console.error('useInvalidateQueries', e));

    hidePoppers();
  }, [currentTabId, patientTab, caseId]);
}

function hidePoppers() {
  // fixes Bug 44275: Date picker doesn't close on page switching
  // Close all poppers when switching between tabs
  const poppers = [
    ...document.getElementsByClassName('MuiPickersPopper-root'),
    ...document.getElementsByClassName('base-Popper-root'),
  ];
  for (let i = 0; i < poppers.length; i++) {
    (poppers[i] as any).style.display = 'none';
  }
}
